import React from 'react';
import MediaCover from '@opm/components/MediaCover';
import ChannelLabel from '@op/components/ChannelLabel';
import { DBO_U, DBO_V } from '@op/utils/format';
import { LanguageType } from '@op/types/common';
import TextClamp from '@opm/components/TextClamp';
import URLShare from '@opm/components/URLShare';
import useIntls from '@opm/hooks/useIntls';
import { DBOM_CONTENT } from '@opm/utils';
import moment from 'moment';
import { BILIBILI_CARD_METRICS } from '../constants';
import './index.scss';

interface IBilibiliVideoCardProps {
  /** B 站视频单条数据 */
  item: any;
  /** 语言切换：origin | en | zh */
  language?: LanguageType;
}

/**
 * Bilibili 视频卡片 — 4 列网格中的单卡片
 *
 * 结构（参考 Figma 4222-67435）：
 *  1. Header: ChannelLabel(bilibili icon) + author_name + 分享按钮
 *  2. Meta: channel_name | video_tag | publish_time（竖线分隔）
 *  3. Title: video_title 3 行截断 + hover 浮窗展开
 *  4. Cover: 封面 + duration 徽章（API 暂无 duration，后端补齐前不展示）
 *  5. Metrics: Views / 弹幕数 / Likes 三项
 */
export default function BilibiliVideoCard({
  item,
  language = 'origin',
}: IBilibiliVideoCardProps) {
  const { intl, isEn } = useIntls();

  const title = DBOM_CONTENT.videoTitle(language, item);
  const channelText = DBOM_CONTENT.channelNameGlobal(isEn, item);
  const tagText = DBOM_CONTENT.videoTagGlobal(isEn, item);
  const publishTime = item?.publish_time
    ? moment(item.publish_time).format('YYYY-MM-DD HH:mm')
    : '';

  const cover = item?.video_cover || '';
  // duration 等待后端补齐；当前 API 不返回，存在时才展示徽章
  const duration = item?.video_duration;

  return (
    <div className="dbom-bili-video-card">
      {/* Header: ChannelLabel(bilibili icon) + author_name + 分享 */}
      <div className="dbom-bili-video-card-header">
        <div className="dbom-bili-video-card-header-channel-label" onClick={() => {
          DBO_U.open(item?.author_url || '');
        }}>
          <ChannelLabel
            className="dbom-bili-video-card-header-channel"
            channel_name="bilibili"
            replaceText={item?.author_name || ''}
          />
        </div>
        <URLShare
          url={item?.video_url}
          className="dbom-bili-video-card-share"
          iconClassName="dbom-bili-video-card-share-icon"
        />
      </div>

      {/* Meta: channel_name | video_tag | publish_time */}
      <div className="dbom-bili-video-card-meta">
        {channelText && (
          <span className="dbom-bili-video-card-meta-item">{channelText}</span>
        )}
        {tagText && (
          <span className="dbom-bili-video-card-meta-item">{tagText}</span>
        )}
        {publishTime && (
          <span className="dbom-bili-video-card-meta-item">{publishTime}</span>
        )}
      </div>

      {/* Title: 3 行截断 + hover 浮窗 */}
      <TextClamp
        rowNum={3}
        text={title}
        placement="top"
        className="dbom-bili-video-card-title"
      />

      {/* Cover + duration 徽章 */}
      <MediaCover
        coverUrl={cover}
        duration={duration}
        className="dbom-bili-video-card-cover"
        coverClassName="dbom-bili-video-card-cover-img"
        durationClassName="dbom-bili-video-card-cover-duration"
        mode="image"
      />

      {/* Metrics: Views / 弹幕数 / Likes */}
      <div className="dbom-bili-video-card-metrics">
        {BILIBILI_CARD_METRICS.map((metric) => {
          const formatFn = metric.format || DBO_V.reviews;
          const value = item?.[metric.key];
          return (
            <div key={metric.key} className="dbom-bili-video-card-metrics-item">
              <div className="dbom-bili-video-card-metrics-value">
                {formatFn(value)}
              </div>
              <div className="dbom-bili-video-card-metrics-label">
                {intl(metric.label)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
